<template>
  <div>
    <b-table
      :ref="tableRef"
      :striped="config.isStriped"
      :hoverable="config.isHoverable"
      :debounce-search="config.searchDebounce"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :per-page="config.perPage"
      :sticky-header="config.stickyHeader"
      :current-page="currentPagedSortedParameter.pageNumber"
      :pagination-position="config.paginationPosition"
      paginated
      backend-pagination
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      backend-sorting
      sort-multiple
      :sort-multiple-data="buefySortingPriority"
      :total="total"
      :data="list"
      :loading="isLoading"
      @sort="onSort"
      @sorting-priority-removed="sortingPriorityRemoved"
      @page-change="onPageChange"
    >
      <b-table-column
        v-for="column in columns"
        :key="column.field"
        v-bind="column"
        :visible="column.visible"
      >
        <template #default="props">
          <div
            v-if="column.field === 'nomProjet'"
            class="clickable"
            @click="showUserDetailsPage($event, props.row)"
          >
            {{ props.row[column.field] }}
          </div>
          <div
            v-else-if="column.field !== 'action'"
            class="clickable"
            @click="showUserDetailsPage($event, props.row)"
          >
            <ul>
              <li v-for="(v, index) in props.row[column.field]" :key="column.field + '-' + index">
                {{ v }}
              </li>
            </ul>
          </div>
          <div v-else class="clickable" @click="showUserDetailsPage($event, props.row)">
            <ul>
              <li v-for="(v, index) in props.row['actions']" :key="'action-' + index">- {{ v }}</li>
            </ul>
          </div>
        </template>
      </b-table-column>
    </b-table>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import {
  FilterDTO,
  ModificationPPDTO,
  ModificationsPPResponseDTO,
  PaginationOrder,
  PaginationParameter,
  UtilisateurDTO,
  UtilisateurModifieDTO,
} from "@/model/bean/GeneratedDTOs"
import Column from "@/views/tables/Column"
import { EnumHelper } from "@/model/bean/EnumHelper"
import { GraeService } from "@/services/rest/grae/GraeService"
import { useTableView } from "@/hooks/useTableView"
import { SortOrder } from "@/model/SortOrder"
import { BTable, BTableColumn } from "buefy"

import DateUtils from "@/services/DateUtils"

export type DataModifPP = {
  nomProjet: string
  nomEtablissement: Array<string>
  uai: Array<string>
  actions: Array<string>
  projet: string
  projetAnnuel: string
}

export default defineComponent({
  name: "ModifProjetView",
  components: {
    BTable,
    BTableColumn,
  },
  props: {
    graeId: {
      type: String,
      required: false,
      default: null,
    },
    loggedUser: {
      type: Object as PropType<UtilisateurDTO>,
      required: false,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      graeService: GraeService.INSTANCE,
      list: [] as Array<DataModifPP>,
      prevListActions: new Map() as Map<number, string>,
      tableView: null as any,
    }
  },
  computed: {
    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },
    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },
    currentPagedSortedParameter(): PaginationParameter {
      return this.tableView?.state.currentPagedSortedParameter || new PaginationParameter()
    },
    activeFilters(): Array<FilterDTO> {
      return this.tableView?.state.activeFilters || []
    },
    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },
    total(): number {
      return this.tableView?.state.total || 0
    },
    config(): any {
      return this.tableView?.state.config || {}
    },
    tableRef(): string {
      return this.tableView?.state.tableRef || ""
    },
  },
  created(): void {
    this.tableView = useTableView({
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      tableRef: "tableModifProjet",
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
    })
  },
  mounted(): void {
    this.tableView?.initMounted()
  },
  methods: {
    onPageChange(page: string | number): void {
      const pageNum = typeof page === "string" ? parseInt(page) : page
      this.tableView?.onPageChange(pageNum)
    },
    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView?.onSort(field, order)
      }
    },
    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView?.sortingPriorityRemoved(field)
      }
    },
    updateTotal(total: number): void {
      this.tableView?.updateTotal(total)
    },
    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },
    async reloadData(): Promise<void> {
      try {
        const id = this.$route.params.graeId
        const response: ModificationsPPResponseDTO = await this.graeService.getModifsPP(id)

        const data: Array<DataModifPP> = []
        response.modificationsPPDTO.forEach((el: ModificationPPDTO) => {
          const userModifieDTO: DataModifPP = {
            nomProjet: el.nomProjet,
            nomEtablissement: el.nomEtablissement,
            uai: el.uai,
            actions: this.formatValueAction(el),
            projet: el.projet,
            projetAnnuel: el.projetAnnuel,
          }
          data.push(userModifieDTO)
        })

        this.list = data
        this.updateTotal(data.length)
      } catch (error) {
        console.error(error)
      }
    },
    getInitialColumns(): Array<Column> {
      return [
        {
          field: "nomProjet",
          label: this.$t("graePage.nomProjet").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "nomEtablissement",
          label: this.$t("graePage.nomEtablissement").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "uai",
          label: this.$t("graePage.uai").toString(),
          tooltip: this.$t("graePage.uai").toString(),
          sortable: false,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allProfilsUtilisateur,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "action",
          label: this.$t("modifProjet.modification").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
      ]
    },
    formatValueLineAction(row: UtilisateurModifieDTO, typeAction: string): string {
      return `${typeAction} ${row.prenom} ${row.nom} - ${DateUtils.formatDateFromPattern(row.lastDateModification, "dd/MM/yyyy HH:mm:ss")}`
    },
    getNextMapIndex(date: Date): number {
      let index: number = new Date(date).getTime()
      do {
        index = index + 1
      } while (this.prevListActions.has(index))
      return index
    },
    formatValueAction(modificationPP: ModificationPPDTO): Array<string> {
      this.prevListActions = new Map()
      const listActions: Array<string> = []

      if (modificationPP.ajoutEnseignants.length > 0) {
        modificationPP.ajoutEnseignants.forEach((el: UtilisateurModifieDTO) =>
          this.prevListActions.set(
            this.getNextMapIndex(el.lastDateModification),
            this.formatValueLineAction(el, "ajout enseignant"),
          ),
        )
      }

      if (modificationPP.retraitEnseigant.length > 0) {
        modificationPP.retraitEnseigant.forEach((el: UtilisateurModifieDTO) =>
          this.prevListActions.set(
            this.getNextMapIndex(el.lastDateModification),
            this.formatValueLineAction(el, "retrait enseignant"),
          ),
        )
      }

      if (modificationPP.ajoutReferent.length > 0) {
        modificationPP.ajoutReferent.forEach((el: UtilisateurModifieDTO) =>
          this.prevListActions.set(
            this.getNextMapIndex(el.lastDateModification),
            this.formatValueLineAction(el, "ajout référent"),
          ),
        )
      }

      if (modificationPP.retraitReferent.length > 0) {
        modificationPP.retraitReferent.forEach((el: UtilisateurModifieDTO) =>
          this.prevListActions.set(
            this.getNextMapIndex(el.lastDateModification),
            this.formatValueLineAction(el, "retrait référent"),
          ),
        )
      }

      // Tri de la liste
      const sorted = new Map(
        Array.from(this.prevListActions).sort(([a], [b]) => (a > b ? -1 : a < b ? 1 : 0)),
      )

      // Ajout dans la liste définitive
      sorted.forEach((el) => listActions.push(el))

      return listActions
    },
    getDefaultSort(): Array<PaginationOrder> {
      const nameSort = new PaginationOrder()
      nameSort.clause = "nomProjet"
      nameSort.desc = true
      const profilSort = new PaginationOrder()
      profilSort.clause = "nomEtablissement"
      profilSort.desc = true
      return [nameSort, profilSort]
    },
    showUserDetailsPage(event: Event, row: any): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()

      const url = `/project/${row["projet"]}/${row["projetAnnuel"]}/historique/0`

      this.$router.push(url)

      //For APP to open ProfileModale
    },
  },
})
</script>
